import Box from '@mui/material/Box';
import type { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import {
    projectMeasureParts,
    type PhaseEntry,
    type ProjectDocType
} from '@ast-data/shared';

/**
 * The project label as a node with its Maßnahmennummer bold and the Kurztitel
 * regular. Shared by the Projekt-Gruppen and Teams boards.
 *
 * `compactBelow` drops the Kurztitel below that breakpoint, leaving the bare
 * Maßnahmennummer — for narrow chrome like the field app bar. It hides in CSS
 * rather than by measuring, so there is no flicker on first paint.
 */
export function ProjectLabel({
    project,
    compactBelow
}: {
    project: ProjectDocType;
    compactBelow?: Breakpoint;
}) {
    const parts = projectMeasureParts({
        baseMeasureNumber: project.baseMeasureNumber,
        subMeasure: project.subMeasure,
        shortTitle: project.shortTitle,
        phases: (project.phases ?? []) as PhaseEntry[]
    });
    if (!parts.measureNumber && !parts.shortTitle) {
        return <>{project.id}</>;
    }
    const shortTitleSx: SxProps<Theme> | undefined = compactBelow
        ? { display: { xs: 'none', [compactBelow]: 'inline' } }
        : undefined;
    return (
        <>
            {parts.measureNumber && (
                <Box component="span" sx={{ fontWeight: 600 }}>
                    {parts.measureNumber}
                </Box>
            )}
            {parts.shortTitle && (
                // The separator travels WITH the Kurztitel, so hiding it can't
                // leave a dangling " - " behind the number.
                <Box component="span" sx={shortTitleSx}>
                    {parts.measureNumber ? ' - ' : ''}
                    {parts.shortTitle}
                </Box>
            )}
        </>
    );
}
